import { AuthContext } from '@urbanx/agx.auth.client';
import { useContext, useEffect, useRef, useState } from 'react';
import {
  ContractActionRequestContractAction,
  DocumentTypes,
  StaffGetContractSettlementResponseSettlementType as SettlementType,
  StaffUpdateContractSettlementRequestSettlementType,
  type StaffUpdateContractSettlementRequest,
  UploadedDocument,
  useInvalidateUseStaffGetContract,
  useInvalidateUseStaffGetContractSettlement,
  usePerformContractAction,
  useStaffGetContractSettlement,
  useStaffUpdateContractSettlement,
} from '@urbanx/agx.campaigns.client';
import {
  TaskBlockerBlockerType,
  useInvalidateUseGetAutopilotTasksForCampaign,
  useReconcileSettlementTask,
} from '@urbanx/agx.activities.client';
import { AxiosProgressEvent, GenericAbortSignal } from 'axios';
import { uploadFile } from 'services';
import { toDateOnlyString, useToast } from '@urbanx/agx.ui-components2';
import {
  buildSettlementContractDocuments,
  getInitialSettlementDate,
  hasPersistedDocumentBeenRemoved,
} from './settlementFormState';

interface Props {
  dialogOpenChangeHandler: (value: boolean) => void;
  taskId: string;
  contractId?: string;
  campaignId?: string;
  isOpen: boolean;
  onTasksInvalidated?: () => void | Promise<void>;
}

const isFile = (file: UploadedDocument | File | undefined): file is File =>
  typeof File !== 'undefined' && file instanceof File;

export const useMarkedAsSettled = ({
  taskId,
  contractId,
  campaignId,
  isOpen,
  dialogOpenChangeHandler,
  onTasksInvalidated,
}: Props) => {
  const { staffUpdateContractSettlement } = useStaffUpdateContractSettlement({
    onSuccess: () => {},
    onError: () => {},
  });
  const { getAuthToken } = useContext(AuthContext);
  const { pushToast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isAwaitingTaskReconciliation, setIsAwaitingTaskReconciliation] = useState(false);
  const submissionInFlight = useRef(false);
  const contractMarkedAsSettled = useRef(false);
  const invalidateTasks = useInvalidateUseGetAutopilotTasksForCampaign();
  const invalidateContract = useInvalidateUseStaffGetContract();
  const invalidateContractSettlement = useInvalidateUseStaffGetContractSettlement();
  const { performContractAction } = usePerformContractAction({
    onSuccess: () => {},
    onError: () => {},
  });
  const { reconcileSettlementTask } = useReconcileSettlementTask({
    onSuccess: () => {},
    onError: () => {},
  });
  const { data: settlement } = useStaffGetContractSettlement(
    campaignId ?? '',
    contractId ?? '',
    !!campaignId && !!contractId
  );
  const [settlementDate, setSettlementDate] = useState<Date | null>(null);
  const [settlementType, setSettlementType] = useState(SettlementType.Pexa);
  const [vendorConfirmationFiles, setVendorConfirmationFiles] = useState<
    (UploadedDocument | File)[]
  >([]);
  const [buyerConfirmationFiles, setBuyerConfirmationFiles] = useState<(UploadedDocument | File)[]>(
    []
  );
  const [pexaConfirmationFiles, setPexaConfirmationFiles] = useState<(UploadedDocument | File)[]>(
    []
  );

  const buildRenamedFormData = (formData: FormData) => {
    const file = formData.get('File');
    if (!file || typeof File === 'undefined' || !(file instanceof File)) {
      return formData;
    }

    const renamedFile = new File([file], `${contractId}/${file.name}`, {
      type: file.type,
      lastModified: file.lastModified,
    });
    const renamedFormData = new FormData();
    renamedFormData.append('File', renamedFile);
    return renamedFormData;
  };

  const onUploadFile = async (
    authToken: string,
    file: string | FormData,
    uploadCampaignId: string,
    uploadedFormType: DocumentTypes,
    uploadProgress?: (progressEvent: AxiosProgressEvent) => void,
    abortSignal?: GenericAbortSignal,
    uploadContractId?: string
  ) =>
    uploadFile(
      authToken,
      typeof FormData !== 'undefined' && file instanceof FormData
        ? buildRenamedFormData(file)
        : file,
      uploadCampaignId,
      uploadedFormType,
      uploadProgress ?? null,
      abortSignal ?? null,
      undefined,
      uploadContractId
    );

  useEffect(() => {
    if (!isOpen) return;

    setSettlementDate(getInitialSettlementDate(settlement?.actualSettlementDate));
    setSettlementType(settlement?.settlementType ?? SettlementType.Pexa);
    setVendorConfirmationFiles(
      settlement?.vendorSettlementConfirmation ? [settlement.vendorSettlementConfirmation] : []
    );
    setBuyerConfirmationFiles(
      settlement?.buyerSettlementConfirmation ? [settlement.buyerSettlementConfirmation] : []
    );
    setPexaConfirmationFiles(
      settlement?.pexaSettlementConfirmation ? [settlement.pexaSettlementConfirmation] : []
    );
  }, [isOpen, settlement]);

  const hasRequiredConfirmation =
    settlementType === SettlementType.Pexa
      ? pexaConfirmationFiles.length > 0
      : vendorConfirmationFiles.length > 0 && buyerConfirmationFiles.length > 0;
  const canComplete = settlementDate != null && hasRequiredConfirmation;

  const uploadSelectedFile = async (
    authToken: string,
    documentType: DocumentTypes,
    selectedFile: File
  ) => {
    if (!campaignId) throw new Error('Campaign is required to upload a settlement confirmation');

    const formData = new FormData();
    formData.append('File', selectedFile);
    const uploadedDocument = await onUploadFile(
      authToken,
      formData,
      campaignId,
      documentType,
      undefined,
      undefined,
      contractId
    );
    if (!uploadedDocument) {
      throw new Error(`Failed to upload ${documentType}`);
    }

    return uploadedDocument as UploadedDocument;
  };

  const persistSettlementChanges = async (
    refreshWhenUnchanged = false,
    markContractAsSettled = false
  ) => {
    if (!campaignId || !contractId || submissionInFlight.current) return;

    submissionInFlight.current = true;
    setIsSubmitting(true);
    let submissionStep: 'details' | 'upload' | 'settlement' | 'reconciliation' = 'details';

    try {
      if (markContractAsSettled && contractMarkedAsSettled.current) {
        submissionStep = 'reconciliation';
        await reconcileTask(campaignId, contractId, taskId);
        await refreshSettledContract(campaignId, contractId);
        dialogOpenChangeHandler(false);
        clearSelection();
        return;
      }

      const selectedSettlementDate = toDateOnlyString(settlementDate);
      const currentSettlementDate = toDateOnlyString(settlement?.actualSettlementDate);
      const settlementDateDirty = selectedSettlementDate !== currentSettlementDate;
      const settlementTypeDirty = settlementType !== settlement?.settlementType;
      const selectedFiles = [
        {
          file: pexaConfirmationFiles[0],
          documentType: DocumentTypes.PexaSettlementConfirmation,
          setFiles: setPexaConfirmationFiles,
        },
        {
          file: vendorConfirmationFiles[0],
          documentType: DocumentTypes.VendorSettlementConfirmation,
          setFiles: setVendorConfirmationFiles,
        },
        {
          file: buyerConfirmationFiles[0],
          documentType: DocumentTypes.BuyerSettlementConfirmation,
          setFiles: setBuyerConfirmationFiles,
        },
      ];
      const filesToUpload = selectedFiles.filter(
        (selectedFile): selectedFile is (typeof selectedFiles)[number] & { file: File } =>
          isFile(selectedFile.file)
      );
      const confirmationRemoved =
        hasPersistedDocumentBeenRemoved(
          vendorConfirmationFiles[0],
          settlement?.vendorSettlementConfirmation
        ) ||
        hasPersistedDocumentBeenRemoved(
          buyerConfirmationFiles[0],
          settlement?.buyerSettlementConfirmation
        ) ||
        hasPersistedDocumentBeenRemoved(
          pexaConfirmationFiles[0],
          settlement?.pexaSettlementConfirmation
        );
      const hasChanges =
        settlementDateDirty ||
        settlementTypeDirty ||
        confirmationRemoved ||
        filesToUpload.length > 0;
      const authToken = filesToUpload.length > 0 ? await getAuthToken() : undefined;
      if (filesToUpload.length > 0 && !authToken) {
        throw new Error('Unable to authenticate confirmation upload');
      }

      if (settlementDateDirty || settlementTypeDirty || confirmationRemoved) {
        const body: StaffUpdateContractSettlementRequest = {};
        if (settlementDateDirty) body.actualSettlementDate = selectedSettlementDate;
        if (settlementTypeDirty) {
          body.settlementType =
            settlementType === SettlementType.Pexa
              ? StaffUpdateContractSettlementRequestSettlementType.Pexa
              : StaffUpdateContractSettlementRequestSettlementType.InPerson;
        }
        if (confirmationRemoved) {
          body.contractDocuments = buildSettlementContractDocuments({
            vendorConfirmation: vendorConfirmationFiles[0],
            originalVendorConfirmation: settlement?.vendorSettlementConfirmation,
            buyerConfirmation: buyerConfirmationFiles[0],
            originalBuyerConfirmation: settlement?.buyerSettlementConfirmation,
            pexaConfirmation: pexaConfirmationFiles[0],
            originalPexaConfirmation: settlement?.pexaSettlementConfirmation,
          });
        }
        await staffUpdateContractSettlement({ campaignId, contractId, body });
      }

      if (authToken) {
        submissionStep = 'upload';
        for (const { file, documentType, setFiles } of filesToUpload) {
          const uploadedDocument = await uploadSelectedFile(authToken, documentType, file);
          setFiles([uploadedDocument]);
        }
      }

      if (hasChanges || refreshWhenUnchanged) {
        await invalidateContractSettlement(campaignId, contractId);
        if (!markContractAsSettled) {
          await invalidateTasks(campaignId, contractId);
          await onTasksInvalidated?.();
        }
      }

      if (markContractAsSettled) {
        submissionStep = 'settlement';
        await performContractAction({
          campaignId,
          contractId,
          contractAction: ContractActionRequestContractAction.MarkSettled,
        });
        contractMarkedAsSettled.current = true;
        setIsAwaitingTaskReconciliation(true);
        submissionStep = 'reconciliation';
        await reconcileTask(campaignId, contractId, taskId);

        try {
          await refreshSettledContract(campaignId, contractId);
        } catch {
          pushToast({
            type: 'error',
            message:
              'The contract was marked as settled, but the latest status could not be loaded. Refresh the page to see the update.',
          });
        }
      }

      dialogOpenChangeHandler(false);
      clearSelection();
    } catch {
      const message =
        submissionStep === 'upload'
          ? 'The settlement confirmation could not be uploaded. Check the file and try again.'
          : submissionStep === 'reconciliation'
            ? 'The contract was marked as settled, but the task blockers could not be updated. Try the task update again.'
            : submissionStep === 'settlement'
              ? 'The contract could not be marked as settled. Your settlement details have been kept so you can try again.'
              : 'The settlement details could not be saved. Check your connection and try again.';
      pushToast({ type: 'error', message });
    } finally {
      submissionInFlight.current = false;
      setIsSubmitting(false);
    }
  };

  const reconcileTask = async (campaignId: string, contractId: string, taskId: string) => {
    const updatedTask = await reconcileSettlementTask({ campaignId, contractId, taskId });
    if (
      updatedTask.blockers?.some(
        (blocker) => blocker.blockerType === TaskBlockerBlockerType.NotMarkedAsSettled
      )
    ) {
      throw new Error('Settlement task reconciliation did not remove the blocker');
    }
  };

  const refreshSettledContract = async (campaignId: string, contractId: string) => {
    await invalidateContract(contractId);
    await invalidateTasks(campaignId, contractId);
    await onTasksInvalidated?.();
  };

  const onComplete = async () => {
    if (isSubmitting || !canComplete) return;
    await persistSettlementChanges(true, true);
  };

  const onSaveAndExit = async () => {
    if (contractMarkedAsSettled.current) return;
    await persistSettlementChanges();
  };

  const clearSelection = () => {
    contractMarkedAsSettled.current = false;
    setIsAwaitingTaskReconciliation(false);
    setSettlementDate(null);
    setSettlementType(SettlementType.Pexa);
    setVendorConfirmationFiles([]);
    setBuyerConfirmationFiles([]);
    setPexaConfirmationFiles([]);
  };

  return {
    getAuthToken,
    onUploadFile,
    settlementDate,
    setSettlementDate,
    settlementType,
    setSettlementType,
    vendorConfirmationFiles,
    setVendorConfirmationFiles,
    buyerConfirmationFiles,
    setBuyerConfirmationFiles,
    pexaConfirmationFiles,
    setPexaConfirmationFiles,
    isSubmitting,
    isAwaitingTaskReconciliation,
    canComplete,
    onComplete,
    clearSelection,
    onSaveAndExit,
  };
};
